import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetCurrentNpsCampaignQueryKey,
  useGetCurrentNpsCampaign,
} from '@area-restrita/api-client/endpoints/nps/nps.gen';
import { MessageSquareText } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';

import IntroStep from './IntroStep';
import ResponseStep from './ResponseStep';
import ScheduleStep from './ScheduleStep';
import SummaryStep from './SummaryStep';
import type { NpsResearchStep, NpsResponseSummary, NpsSchedule } from './types';

const NpsResearch: React.FC = () => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<NpsResearchStep>('intro');
  const [selectedSchedules, setSelectedSchedules] = useState<NpsSchedule[]>([]);
  const [answeredCampaignIds, setAnsweredCampaignIds] = useState<string[]>([]);
  const [summary, setSummary] = useState<NpsResponseSummary | null>(null);
  const { data: currentCampaign, refetch } = useGetCurrentNpsCampaign();
  const campaign = currentCampaign?.campaign ?? null;
  const displayedCampaign = campaign ?? summary?.campaign ?? null;
  const isPending = Boolean(
    campaign &&
      !currentCampaign?.answered &&
      !answeredCampaignIds.includes(campaign.id),
  );

  if (!displayedCampaign || (!isPending && step !== 'summary')) return null;

  const close = () => {
    setOpen(false);
    if (step !== 'summary') setStep('intro');
  };

  const finish = () => {
    setOpen(false);
    setStep('intro');
    setSummary(null);
    setSelectedSchedules([]);
  };

  const handleSuccess = (nextSummary: NpsResponseSummary) => {
    setAnsweredCampaignIds((current) => [...current, nextSummary.campaign.id]);
    setSummary(nextSummary);
    setStep('summary');
    refetch();
    queryClient.invalidateQueries({
      queryKey: getGetCurrentNpsCampaignQueryKey(),
    });
  };

  return (
    <>
      {isPending ? (
        <div className="flex flex-col gap-4 rounded-lg border border-primary/40 bg-primary/5 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-3">
            <MessageSquareText className="mt-1 size-5 shrink-0 text-primary" />
            <div>
              <p className="font-semibold">Pesquisa de satisfação</p>
              <p className="text-sm text-muted-foreground">
                Ajude-nos a melhorar o seu transporte. Leva menos de 1 minuto.
              </p>
            </div>
          </div>
          <Button onClick={() => setOpen(true)}>Responder pesquisa</Button>
        </div>
      ) : null}

      <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && close()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          {step === 'intro' ? (
            <IntroStep
              campaign={displayedCampaign}
              onClose={close}
              onContinue={() => setStep('schedules')}
            />
          ) : null}
          {step === 'schedules' ? (
            <ScheduleStep
              initialSchedules={selectedSchedules}
              onClose={close}
              onContinue={(schedules) => {
                setSelectedSchedules(schedules);
                setStep('response');
              }}
            />
          ) : null}
          {step === 'response' && campaign ? (
            <ResponseStep
              campaign={campaign}
              selectedSchedules={selectedSchedules}
              onChangeSchedules={() => setStep('schedules')}
              onClose={close}
              onStaleCampaign={refetch}
              onSuccess={handleSuccess}
            />
          ) : null}
          {step === 'summary' && summary ? (
            <SummaryStep summary={summary} onClose={finish} />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NpsResearch;
